import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Sparkles, ArrowRight, CheckCircle2, Coins } from "lucide-react";
import { magicChallenge, magicStyles } from "@/lib/magicApi";

export default function MagicChallenge() {
  const [ch, setCh] = useState(null);
  const [style, setStyle] = useState(null);
  const { status } = useOutletContext() || {};

  useEffect(() => {
    magicChallenge().then(async (c) => {
      setCh(c);
      const s = await magicStyles();
      const all = (s.categories || []).flatMap((x) => x.styles);
      setStyle(all.find((x) => x.slug === c.style_slug));
    }).catch(() => toast.error("Couldn't load challenge"));
  }, []);

  if (!ch) return <div className="py-16 text-center text-white/40">Loading…</div>;
  const completed = !!status?.challenge_completed;

  return (
    <div className="space-y-5" data-testid="magic-challenge-page">
      <section className="rounded-3xl border border-[#FFD700]/50 bg-gradient-to-br from-[#FFD700]/25 via-[#FF00E5]/15 to-[#0A0512] p-6 sm:p-8 text-center relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl bg-[#FFD700]/40" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 bg-black/40 border border-[#FFD700]/40 text-[#FFD700] px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3">
            <Trophy className="w-3 h-3" fill="currentColor" /> Daily Magic Challenge
          </div>
          <h1 className="font-black text-3xl sm:text-4xl leading-none">Today's style</h1>
          <div className="font-black text-3xl sm:text-4xl mt-1 text-[#FFD700]">{ch.style_name}</div>
          <div className="text-[11px] uppercase tracking-widest text-white/50 mt-1">{ch.category}</div>

          <div className="mt-6 inline-flex items-center gap-2 bg-black/40 border border-[#FFD700]/40 px-4 py-2 rounded-full">
            <Coins className="w-4 h-4 text-[#FFD700]" fill="currentColor" />
            <span className="font-black text-lg text-[#FFD700]">+{ch.bonus_coins}</span>
            <span className="text-[10px] uppercase tracking-widest text-white/60">bonus coins</span>
          </div>

          {completed ? (
            <div className="mt-5 flex items-center justify-center gap-1.5 text-[#39FF14] font-black text-sm uppercase tracking-widest" data-testid="challenge-done">
              <CheckCircle2 className="w-4 h-4" /> Completed today
            </div>
          ) : (
            <Link to={`/magic/create/${ch.style_slug}?daily=1`} data-testid="challenge-cta"
              className="mt-5 inline-flex items-center gap-1 bg-[#FFD700] text-[#0A0512] font-black text-sm uppercase tracking-widest px-6 py-3 rounded-full shadow-[0_0_25px_rgba(255,215,0,0.5)]">
              <Sparkles className="w-4 h-4" fill="currentColor" /> Play today's challenge <ArrowRight className="w-4 h-4" />
            </Link>
          )}
          {style?.prompt && (
            <div className="mt-6 text-xs text-white/50 italic max-w-md mx-auto leading-relaxed">
              "{style.prompt.split('.')[0]}."
            </div>
          )}
        </div>
      </section>

      <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">How it works</div>
        <ul className="text-sm space-y-1.5 text-white/80">
          <li>· A new style is featured every day at midnight UTC.</li>
          <li>· First transformation you make in that style today wins <b className="text-[#FFD700]">50 coins</b>.</li>
          <li>· Your daily free generations still count — no coin spend needed.</li>
          <li>· Comeback tomorrow for a fresh style and another bonus.</li>
        </ul>
      </section>
    </div>
  );
}
